import type { ReactNode } from 'react';
import type { GateReason } from '@erectus/shared';
import { gateReasonText } from '@/pose/qualityGate.js';
import { Icon } from '@/ui/icons.js';

/**
 * The quality-gate checklist, floating over the camera preview.
 *
 * Full sentences, not abbreviations: this is the thing you read while moving
 * the phone around, and "Hip" does not tell you what to do about it. It sits
 * over the picture rather than inside the settings sheet because opening a
 * panel to find out why the app is unhappy defeats the purpose.
 *
 * It collapses to a single line once every check passes — the detail only
 * matters while something is wrong, and the space belongs to the preview.
 */
export interface GateCheckRow {
  id: string;
  label: string;
  passed: boolean;
}

export function GateChecklist({
  checks,
  reasons,
  loading,
}: {
  checks: readonly GateCheckRow[];
  reasons: readonly GateReason[];
  loading?: boolean;
}): ReactNode {
  const allPassed = !loading && checks.length > 0 && checks.every((c) => c.passed);

  if (loading) {
    return (
      <Panel>
        <p className="text-sm font-medium text-white/90">Starting the camera…</p>
      </Panel>
    );
  }

  if (allPassed) {
    return (
      <Panel tone="good">
        <p className="flex items-center gap-2 text-sm font-semibold text-white">
          <Mark passed />
          Shot looks good — ready to start
        </p>
      </Panel>
    );
  }

  // The most actionable sentence, for the first thing that is wrong.
  const hint = reasons.length > 0 ? gateReasonText(reasons[0]!) : null;

  return (
    <Panel>
      <ul className="space-y-1.5">
        {checks.map((check) => (
          <li key={check.id} className="flex items-center gap-2 text-[13px] leading-tight">
            <Mark passed={check.passed} />
            <span className={check.passed ? 'font-medium text-white/90' : 'font-bold text-white'}>
              {check.label}
            </span>
          </li>
        ))}
      </ul>
      {hint ? <p className="mt-2 text-xs font-medium leading-snug text-white">{hint}</p> : null}
    </Panel>
  );
}

function Panel({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'good';
}): ReactNode {
  return (
    <div
      role="status"
      aria-live="polite"
      data-tone={tone}
      // No panel behind the checks: the camera picture stays visible. A soft
      // shadow keeps white text legible on a bright scene.
      className="px-1 py-1"
      style={{ textShadow: '0 1px 2px rgba(15,23,42,0.95), 0 0 10px rgba(15,23,42,0.6)' }}
    >
      {children}
    </div>
  );
}

function Mark({ passed }: { passed: boolean }): ReactNode {
  return (
    <span
      aria-hidden
      className="grid size-[18px] shrink-0 place-items-center rounded-full text-white"
      style={{
        backgroundColor: passed ? 'var(--color-state-good)' : 'var(--color-state-fhp)',
      }}
    >
      <Icon name={passed ? 'check' : 'alert'} size={12} strokeWidth={3} />
    </span>
  );
}
